import { mascara, numero } from './ValorDoMes';
import React, { useState } from 'react';
import { MetaCompartilhada } from '../services/api';
import PilhaDeAvatares from './PilhaDeAvatares';
import { formatPct } from '../utils/format';
import './MetaCompartilhada.css';

/**
 * O card da meta compartilhada — irmão do card da reserva pessoal.
 *
 * ── Por que mora dentro da aba Reservas, e não numa aba nova ─────────────
 *
 * As abas de Metas separam por TIPO: Reservas × Limites de Gastos.
 * "Compartilhada" não é um tipo — é *quem participa*, que é outro eixo. Uma
 * terceira aba misturaria os dois e criaria um terceiro lugar para procurar
 * "minhas metas".
 *
 * A separação de tabelas que o backend fez por segurança (`meta_compartilhada`
 * nunca encosta em `goals`) fica INVISÍVEL aqui, que é onde ela deve ficar.
 *
 * ── A diferença visível são os avatares ──────────────────────────────────
 *
 * Mesmo `goal-card`, mesma barra, mesmos números. Mais duas coisas: a pilha de
 * iniciais e a linha "você pôs R$ X" — que custa uma linha e já responde a
 * pergunta que a pessoa mais faz sobre a própria meta compartilhada.
 */

/**
 * A barra, com o papel de ARIA que ela precisa ter.
 *
 * `role="progressbar"` sozinho anuncia "barra de progresso" e mais nada —
 * inútil. O `aria-label` carrega o que a barra significa, porque o valor
 * sozinho ("30") não diz de quê nem de quanto.
 */
const Barra: React.FC<{ feito: number; alvo: number; cor: string; rotulo: string }> =
  ({ feito, alvo, cor, rotulo }) => {
    const pct = alvo > 0 ? Math.min((feito / alvo) * 100, 100) : 0;
    return (
      <div
        className="goal-bar-wrap"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={rotulo}
      >
        <div className="goal-bar-fill" style={{ width: `${pct}%`, background: cor }} />
      </div>
    );
  };

export interface AcoesDaMeta {
  aportar: (metaId: string, valor: number) => Promise<void>;
  convidar: (metaId: string, email: string) => Promise<void>;
  cancelarConvite: (metaId: string, membroId: string) => Promise<void>;
  sair: (metaId: string) => Promise<void>;
  encerrar: (metaId: string) => Promise<void>;
}

export const MetaCompartilhadaCard: React.FC<{
  meta: MetaCompartilhada;
  cor?: string;
  acoes: AcoesDaMeta;
  /**
   * Vem de fora, e não é preciosismo.
   *
   * O `fmt` do `Goals.tsx` passa por `brlOrMasked`, que é o que faz o olhinho
   * de "Ocultar valores" alcançar esta seção. Um formatador próprio aqui
   * deixaria o card da meta compartilhada como o ÚNICO lugar do painel que
   * ignora o olhinho — e quem liga o olhinho normalmente tem alguém olhando a
   * tela junto, que é exatamente a situação de uma vaquinha.
   *
   * Vale também para os `aria-label`: mascarar o texto e deixar o número no
   * rótulo entregaria pelo leitor de tela o que o olho não devia ver.
   */
  fmt: (v: number) => string;
  /**
   * Nasce com o "Ver mais" já aberto. Só o exemplo da tela vazia usa: lá o
   * card está esmaecido e sem toque, e fechado ele esconderia justamente o
   * que a vaquinha tem de diferente — quem pôs quanto.
   */
  abertoDeInicio?: boolean;
}> = ({ meta, cor = 'var(--t-p)', acoes, fmt, abertoDeInicio = false }) => {
  const [aberto, setAberto] = useState(abertoDeInicio);
  const [valor, setValor] = useState('');
  const [email, setEmail] = useState('');
  /* O convite abre sozinho, sem precisar abrir o card inteiro — ver a nota no resumo. */
  const [convidando, setConvidando] = useState(false);
  const [convidou, setConvidou] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<'aporte' | 'convite' | 'saida' | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const falta = Math.max(meta.alvo - meta.total, 0);
  const completa = falta <= 0;
  const pct = meta.alvo > 0 ? Math.min((meta.total / meta.alvo) * 100, 100) : 0;
  /* O maior aporte é a régua da barrinha de cada pessoa — não o alvo da meta.
     Contra o alvo, num começo de vaquinha todas as barras ficam invisíveis e a
     comparação que a lista existe para permitir não acontece. */
  const maior = Math.max(...meta.membros.map(m => m.valor ?? 0), 0);

  /**
   * O que fazer quando a ação falha.
   *
   * O valor NÃO é limpo. É a regra do desenho, e ela existe porque a falha
   * mais provável aqui é a rede: limpar o campo faria a pessoa digitar de novo
   * um número que ela já tinha conferido — e na segunda vez ela confere menos.
   * O motivo aparece ao lado do valor que continua lá.
   */
  const tentar = async (
    qual: 'aporte' | 'convite' | 'saida', fn: () => Promise<void>, aoDarCerto?: () => void,
  ) => {
    setOcupado(qual);
    setErro(null);
    try {
      await fn();
      aoDarCerto?.();
    } catch (e: any) {
      setErro(e?.response?.data?.error ?? 'Não consegui agora. Tenta de novo?');
    } finally {
      setOcupado(null);
    }
  };

  const guardar = () => {
    const v = numero(valor);
    if (!Number.isFinite(v) || v <= 0) return setErro('Põe um valor maior que zero.');
    /* Só limpa DEPOIS de dar certo — ver a nota em `tentar`. */
    tentar('aporte', () => acoes.aportar(meta.id, v), () => setValor(''));
  };

  const chamar = (e: React.FormEvent) => {
    e.preventDefault();
    const para = email.trim();
    tentar('convite', () => acoes.convidar(meta.id, para), () => {
      setEmail('');
      setConvidando(false);
      setConvidou(para);
    });
  };

  const sozinha = meta.membros.length <= 1 && meta.convites.length === 0;

  return (
    <div className="goal-card meta-card" style={{ '--accent': cor } as React.CSSProperties}>
      <div className="goal-card-top" style={{ borderTopColor: cor }}>
        {/*
          * O nome é só título (16/09). Antes o cabeçalho inteiro abria o card,
          * com uma seta no canto, e ela não achou: "esse botão de quem pôs e
          * fechar não está muito intuitivo". Abrir agora é o "Ver mais" lá
          * embaixo, escrito.
          */}
        <div className="meta-cabeca">
          <span className="goal-card-name">{meta.nome}</span>
        </div>

        <div className="goal-amounts">
          <span className="goal-saved" style={{ color: completa ? 'var(--s-ok)' : cor }}>
            {fmt(meta.total)}
          </span>
          {/*
            * "/" e não "de" — o mesmo separador do card da reserva pessoal.
            *
            * Duas razões, e as duas vieram do /qa. A primeira é paridade: os
            * dois cards ficam lado a lado na mesma grade, e um dizendo "/" e o
            * outro "de" é a irmandade quebrando no detalhe mais visível.
            *
            * A segunda é espaço, medido: com "de" a linha pedia 236px num card
            * de 216px úteis e o alvo era CORTADO. "/" mais a fonte menor abaixo
            * trazem para 209px.
            *
            * O `aria-label` da barra continua dizendo "de": ali é leitura em voz
            * alta, e "barra" no meio de dois valores não quer dizer nada.
            */}
          <span className="goal-sep">/</span>
          <span className="goal-target">{fmt(meta.alvo)}</span>
        </div>

        <Barra
          feito={meta.total} alvo={meta.alvo}
          cor={completa ? 'var(--s-ok)' : cor}
          rotulo={`${meta.nome}: ${fmt(meta.total)} de ${fmt(meta.alvo)}`}
        />

        {/*
          * Porcentagem à esquerda, "faltam" à direita — a MESMA linha do card
          * da reserva pessoal.
          *
          * Ela tinha ficado de fora, e a diferença apareceu rodando: os dois
          * cards lado a lado com os rótulos em posições diferentes. A feature
          * inteira se apoia em a compartilhada ser IRMÃ da pessoal, e irmã que
          * alinha diferente vira outra coisa.
          *
          * `formatPct` não é mascarado pelo olhinho, igual ao card pessoal: o
          * que ele esconde é quanto ela TEM, e uma proporção do alvo do grupo
          * não diz isso.
          */}
        <div className="goal-bar-labels">
          <span className="goal-pct" style={{ color: completa ? 'var(--s-ok)' : cor }}>
            {formatPct(pct)}
          </span>
          {completa
            ? <span className="goal-done-label">✓ Chegou lá!</span>
            : <span className="goal-remaining">Faltam {fmt(falta)}</span>}
        </div>

        {/*
          * A linha de resumo, sempre visível: quem está junto, e quanto EU pus.
          *
          * "Você pôs" é a pergunta que a pessoa mais faz sobre a própria meta
          * compartilhada, e ela custa uma linha. A lista completa abre no
          * clique — mas quem só passou o olho já saiu com a resposta.
          */}
        <div className="meta-resumo">
          <PilhaDeAvatares pessoas={meta.membros} />
          <span className="meta-meu">
            {meta.meu > 0 ? <>você pôs <strong>{fmt(meta.meu)}</strong></> : 'você ainda não pôs nada'}
          </span>
          {/*
            * Convidar mora AQUI, sempre à vista (16/09).
            *
            * Antes o campo ficava dentro do card aberto, atrás da seta do
            * título, com o placeholder "chamar mais alguém" fazendo papel de
            * rótulo. Ela: "tenho que clicar numa seta e não é muito intuitivo
            * como devo adicionar alguém". Numa meta para juntar dinheiro COM
            * alguém, chamar a pessoa é a primeira coisa a fazer, e não pode
            * depender de descobrir a seta.
            */}
          {!convidando && (
            <button
              type="button"
              className="meta-convidar"
              onClick={() => { setConvidando(true); setConvidou(null); setErro(null); }}
            >
              + Convidar
            </button>
          )}
        </div>

        {sozinha && !convidando && !convidou && (
          <p className="meta-sozinha">Só você por enquanto. Convide quem vai juntar com você.</p>
        )}

        {meta.convites.length > 0 && !aberto && (
          <p className="meta-pendentes">
            {meta.convites.length === 1
              ? `Convite enviado para ${meta.convites[0].email}, esperando resposta.`
              : `${meta.convites.length} convites esperando resposta.`}
          </p>
        )}

        {convidou && !convidando && meta.convites.length === 0 && (
          <p className="meta-pendentes" role="status">Convite enviado para {convidou}.</p>
        )}

        {convidando && (
          <form className="meta-convite-form" onSubmit={chamar}>
            <label className="meta-convite-rotulo" htmlFor={`convite-${meta.id}`}>
              Convidar pelo email
            </label>
            <div className="meta-form">
              <input
                id={`convite-${meta.id}`}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="email@exemplo.com"
                autoFocus
                disabled={ocupado !== null}
              />
              <button className="meta-btn" disabled={ocupado !== null || !email.trim()} aria-busy={ocupado === 'convite'}>
                Enviar convite
              </button>
            </div>
            <p className="meta-convite-ajuda">
              A pessoa recebe um email e decide se entra na meta.{' '}
              <button
                type="button"
                className="meta-convite-cancelar"
                onClick={() => { setConvidando(false); setEmail(''); setErro(null); }}
              >
                Cancelar
              </button>
            </p>
          </form>
        )}

        {aberto && (
          <div className="meta-aberto">
            <h4 className="meta-titulo">Quem pôs no bolo</h4>
            <ul className="meta-lista">
              {meta.membros.map(m => (
                <li key={m.userId ?? m.nome} className={m.valor === null ? 'meta-ainda-nao' : ''}>
                  <span className="meta-nome">{m.nome}</span>
                  {/*
                    * Quem não pôs nada NÃO ganha barra e NÃO ganha R$ 0.
                    *
                    * Um zero com nome ao lado vira placar, e a feature existe
                    * para evitar briga. A pessoa está no grupo e a conta fecha;
                    * o vazio é que não vira número para comparar.
                    *
                    * `m.valor === null` e não `!m.valor`: zero não existe no
                    * livro (`CHECK (valor > 0)`), mas `!m.valor` passaria a
                    * tratar como "ainda não" qualquer valor que virasse zero
                    * por outro caminho — e aí a decisão mudaria sem ninguém
                    * decidir.
                    */}
                  {m.valor === null ? (
                    <span className="meta-vazio">ainda não</span>
                  ) : (
                    <>
                      <Barra
                        feito={m.valor} alvo={maior} cor={cor}
                        rotulo={`${m.nome} pôs ${fmt(m.valor)}`}
                      />
                      <span className="meta-valor">{fmt(m.valor)}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>

            {meta.convites.length > 0 && (
              <>
                <h4 className="meta-titulo">Chamados, sem resposta</h4>
                <ul className="meta-lista meta-convites">
                  {meta.convites.map(c => (
                    <li key={c.id}>
                      <span className="meta-nome">{c.email}</span>
                      <button
                        type="button"
                        className="meta-x"
                        disabled={ocupado !== null}
                        aria-label={`Cancelar o convite para ${c.email}`}
                        onClick={() => tentar('convite',
                          () => acoes.cancelarConvite(meta.id, c.id))}
                      >✕</button>
                    </li>
                  ))}
                </ul>
              </>
            )}


            <div className="meta-rodape">
              <button
                type="button"
                className="meta-sair"
                disabled={ocupado !== null}
                onClick={() => tentar('saida', () => acoes.sair(meta.id))}
              >
                Sair da meta
              </button>
              {meta.souDona && (
                /*
                 * "Encerrar", e nunca "Apagar" — a palavra é o desenho.
                 *
                 * Não existe DELETE: a política de `meta_compartilhada` não tem
                 * verbo de remoção, de propósito, porque apagar destruiria o
                 * registro do dinheiro que outras pessoas guardaram. Um botão
                 * escrito "apagar" prometeria o que o banco recusa.
                 */
                <button
                  type="button"
                  className="meta-encerrar"
                  disabled={ocupado !== null}
                  onClick={() => tentar('saida', () => acoes.encerrar(meta.id))}
                >
                  Encerrar meta
                </button>
              )}
            </div>
          </div>
        )}

        {/*
          * `<button aria-expanded>` e não `div onClick`: Enter e Espaço, o foco
          * visível e o anúncio de "recolhido/expandido" vêm de graça.
          */}
        <button
          type="button"
          className="meta-ver-mais"
          aria-expanded={aberto}
          onClick={() => setAberto(a => !a)}
        >
          {aberto ? 'Ver menos' : 'Ver mais'}
          {/*
            * O mesmo ícone do resto do app (`PaymentMethods`, `SortableSection`),
            * e não "⌄": aquilo é um ACENTO tipográfico, desenha diferente em
            * cada fonte e ela leu como acento — que é o que é.
            */}
          <svg className="meta-ver-mais-seta" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {erro && <p className="meta-erro" role="alert">{erro}</p>}
      </div>

      <div className="goal-deposit-row meta-guardar">
        {/*
          * A mesma máscara dos outros campos de valor (05/10): ela digita só
          * os números e o campo escreve "1.250,00". Era `type="number"`, que
          * no celular abre teclado com ponto e vírgula e aceita "1250" e
          * "12,50" sem dizer qual dos dois entendeu.
          *
          * `type="text"` com `inputMode="numeric"`: teclado só de dígitos, e o
          * navegador não reinterpreta o que a máscara escreveu.
          */}
        <input
          type="text"
          inputMode="numeric"
          placeholder="Guardar dinheiro"
          value={valor}
          onChange={e => setValor(mascara(e.target.value))}
          disabled={ocupado === 'aporte'}
          aria-label={`Quanto guardar em ${meta.nome}`}
          onKeyDown={e => e.key === 'Enter' && ocupado === null && guardar()}
        />
        <button
          type="button"
          className="goal-deposit-save"
          style={{ background: cor }}
          disabled={ocupado !== null || !valor.trim()}
          aria-busy={ocupado === 'aporte'}
          onClick={guardar}
          aria-label={`Guardar dinheiro em ${meta.nome}`}
        >
          +
        </button>
      </div>
    </div>
  );
};

export default MetaCompartilhadaCard;
